import { useEffect, useRef, useState } from "react";

import { useNotification, useWarnAboutChange } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { conflictText } from "@/lib/tina/festivals";
import {
  type ResolveOutcome,
  useFestivalRepository,
} from "@/pages/tina/festivals/hooks/use-festival-repository";
import {
  type PageForm,
  type PageFormAction,
  initialPageForm,
  isDirty,
  pageFormReducer,
  rebaseForm,
} from "@/pages/tina/festivals/lib/page-form";
import {
  type PageMode,
  pageMode,
  refetchEvery,
} from "@/pages/tina/festivals/lib/page-request";
import { festivalPageKey } from "@/pages/tina/festivals/lib/query-keys";
import type { FestivalPageRead } from "@/types/tina-festival-page";

/** What the form was built from: the page it was seeded from, and the form that page seeds (the rebase base). */
interface Session {
  page: FestivalPageRead;
  base: PageForm;
  form: PageForm;
}

/** A reload that kept the curator's edits, and the ones that did not fit. */
export interface PageNotice {
  text: string;
  dropped: string[];
}

/** A 409 after which the page must be read again: the festival is no longer what the write was made against. */
const REFETCH_CONFLICTS = [
  "record_state",
  "suggestion_closed",
  "suggestion_not_found",
];

const statusOf = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null
    ? (error as { statusCode?: number }).statusCode
    : undefined;

/** The fields that, when they change, make the form built from the old page wrong for the new one. */
const sameShape = (a: FestivalPageRead, b: FestivalPageRead): boolean =>
  a.state === b.state &&
  a.kind === b.kind &&
  a.skeleton_digest === b.skeleton_digest &&
  a.page_version === b.page_version;

const eventName = (page: FestivalPageRead, id: string): string =>
  page.existing.find((line) => line.mood_event_id === id)?.name ?? id;

/**
 * Everything the festival page holds and does, so the page itself is only layout.
 *
 * **The form belongs to the page it was built from.** When a refetch brings a page whose state, kind, skeleton digest
 * or page version differ, the form is seeded again when it is clean, else the curator's edits are laid over the new
 * page (`rebaseForm`) and a notice says so: the nightly pass rebuilt the skeleton, or another tab published. `run` is
 * every write: one at a time (a second click while one is on its way does nothing), its answer mapped to what the page
 * shows.
 */
export function useFestivalPage(recordId: string) {
  const repo = useFestivalRepository();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { setWarnWhen } = useWarnAboutChange();

  const query = useQuery({
    queryKey: festivalPageKey(recordId),
    queryFn: () => repo.fetchPage(recordId),
    refetchInterval: (q) => refetchEvery(q.state.data),
    // A page that is gone stays gone: no retry on a 404.
    retry: (count, error) => statusOf(error) !== 404 && count < 2,
  });

  const [session, setSession] = useState<Session | null>(null);
  const [notice, setNotice] = useState<PageNotice | null>(null);
  const [serverProblems, setServerProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Checked and set before the first `await`, so two clicks in one tick see it; the state only draws it.
  const busyRef = useRef(false);

  // The latest session, for the code that must compute the next one outside a state updater.
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  const fetched = query.data;
  useEffect(() => {
    if (!fetched) return;
    const current = sessionRef.current;
    if (current === null) {
      const seeded = initialPageForm(fetched);
      setSession({ page: fetched, base: seeded, form: seeded });
      return;
    }
    if (current.page === fetched) return;
    if (sameShape(current.page, fetched)) {
      setSession({ ...current, page: fetched });
      return;
    }
    const seeded = initialPageForm(fetched);
    if (!isDirty(current.form, current.base)) {
      setNotice(null);
      setSession({ page: fetched, base: seeded, form: seeded });
      return;
    }
    const rebased = rebaseForm(current.base, current.form, seeded, (id) =>
      eventName(current.page, id),
    );
    setNotice({ text: conflictText("page_changed"), dropped: rebased.dropped });
    setSession({ page: fetched, base: seeded, form: rebased.form });
  }, [fetched]);

  const dirty = session ? isDirty(session.form, session.base) : false;
  useEffect(() => {
    setWarnWhen(dirty);
  }, [dirty, setWarnWhen]);
  useEffect(() => () => setWarnWhen(false), [setWarnWhen]);

  const dispatch = (action: PageFormAction): void => {
    setSession((current) =>
      current
        ? { ...current, form: pageFormReducer(current.form, action) }
        : current,
    );
    setServerProblems([]);
  };

  /** Take an answered page into the cache, and into the form when it is this festival's. */
  function takePage(
    answered: FestivalPageRead,
    after: "seed" | "rebase",
  ): void {
    queryClient.setQueryData(festivalPageKey(answered.record_id), answered);
    if (answered.record_id !== recordId) return;
    const current = sessionRef.current;
    const seeded = initialPageForm(answered);
    if (current === null || after === "seed") {
      setNotice(null);
      setSession({ page: answered, base: seeded, form: seeded });
      return;
    }
    const rebased = rebaseForm(current.base, current.form, seeded, (id) =>
      eventName(current.page, id),
    );
    setNotice(
      rebased.dropped.length > 0
        ? {
            text: "Saved. Some of your edits no longer fit.",
            dropped: rebased.dropped,
          }
        : null,
    );
    setSession({ page: answered, base: seeded, form: rebased.form });
  }

  async function run<T>(
    write: () => Promise<ResolveOutcome<T>>,
    after: "seed" | "rebase",
    pageOf: (data: T) => FestivalPageRead = (data) => data as FestivalPageRead,
    /** Where a 422 goes when it is not about the page's own body (a suggestion's answer); default: the page's list. */
    onRefused?: (problems: string[]) => void,
  ): Promise<ResolveOutcome<T> | null> {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    try {
      let outcome: ResolveOutcome<T>;
      try {
        outcome = await write();
      } catch {
        // The repository showed it and sent it to Sentry.
        return null;
      }
      switch (outcome.kind) {
        case "ok":
          setServerProblems([]);
          takePage(pageOf(outcome.data), after);
          break;
        case "stale":
          // The festival changed under the curator: read it again, and the effect lays the edits over it.
          await query.refetch();
          break;
        case "refused":
          if (onRefused) onRefused(outcome.problems);
          else setServerProblems(outcome.problems);
          break;
        case "conflict":
          open?.({ type: "error", message: conflictText(outcome.detail) });
          if (
            REFETCH_CONFLICTS.includes(outcome.detail.split(":")[0]) ||
            outcome.detail.startsWith("record_state")
          ) {
            await query.refetch();
          }
          break;
        case "no_identity":
          break;
      }
      return outcome;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const page = fetched;
  const mode: PageMode | null = page ? pageMode(page) : null;
  return {
    query,
    page,
    mode,
    form: session?.form ?? null,
    base: session?.base ?? null,
    dispatch,
    dirty,
    notice,
    dismissNotice: () => setNotice(null),
    serverProblems,
    busy,
    run,
  };
}
